import { makeObservable, computed, observable, action, reaction, ObservableMap } from 'mobx';
import { character } from '@api';
import { FormStore } from '../formstore/FormStore';
import { CharacterAdapter } from './CharacterAdapter';
import { get, set } from 'lodash'

const USER_DEFINE_GROUP_KEY = 'Userdefinegroup'
export class CharacterStore {
  characteristicsIdMap: ObservableMap
  characteristicsDetailMap: ObservableMap
  characterFormStore?: FormStore
  characterAdapter: CharacterAdapter
  constructor() {
    this.characteristicsIdMap = observable.map()
    this.characteristicsDetailMap = observable.map()
    this.characterAdapter = new CharacterAdapter()
    makeObservable(this, {
      getCharacterDesc: action.bound,
      genCharacterMap: action.bound,
      findDetail: action.bound,
      characterDetail: computed
    })
  }

  /**
   * 获取特征组描述信息
   * @param productIds string[] 物料id集合
   * @param types string[] 特征组类型集合
   * @returns promise 请求结果
   */
  getCharacterDesc = async (productIds: string[], types: string[]) => {
    const data = {
      products: productIds,
      characteristicsTypes: types,
    }
    const res = await Promise.all([
      character({ data })
      // getSensitiveCharactersByProduct,
    ]);
    this.genCharacterMap(res)
    return res
  }

  /**
   * 转换接口返回的描述信息结构
   * @param characterDesc 特征接口返回信息
   */
  genCharacterMap = (characterDesc: any) => {
    const characterData = characterDesc[0].data;
    if (characterData) {
      // { 特征组详细描述, 物料模版与特征组的对应关系 }
      const { characteristicsDetail, products } = characterData;
      // 结构转换，方便查找, 用物料id和特征组类型查找特征组id，productId+type -> characteristicsId
      // const characteristicsIdMap = new Map()
      for (const product of products) {
        const { productIds, FreeCT, OptionCT, template } = product
        for (const productId of productIds) {
          if (template) this.characteristicsIdMap.set(`${productId}-template`, template)
          if (FreeCT) this.characteristicsIdMap.set(`${productId}-FreeCT`, FreeCT)
          if (OptionCT) this.characteristicsIdMap.set(`${productId}-OptionCT`, OptionCT)
        }
      }
      // 同上，用characteristicsId查找特征组详细描述
      // const characteristicsDetailMap = new Map()
      for (const detail of characteristicsDetail) {
        this.characteristicsDetailMap.set(detail.characteristicsId, detail)
      }
    }
  }

  /**
   * 根据参照id和特征组类型，获取特征组详细描述
   * @param productId
   * @param type
   * @returns object 特征组详细描述
   */
  findDetail = async (productId: string, type: string) => {
    const detailId = this.characteristicsIdMap.get(`${productId}-${type}`)
    if (detailId && this.characteristicsDetailMap.get(detailId)) {
      // 已经请求过数据
      return this.characteristicsDetailMap.get(detailId)
    } else {
      // 参照模版未绑定特征组或还未请求数据
      await this.getCharacterDesc([productId], [type])
      const detailId = this.characteristicsIdMap.get(`${productId}-${type}`)
      return this.characteristicsDetailMap.get(detailId)
    }
  }

  get characterDetail() {
    return (productId: string, type: string) => {
      const detailId = this.characteristicsIdMap.get(`${productId}-${type}`)
      if (detailId) return this.characteristicsDetailMap.get(detailId)
    }
  }

  /**
   * 将特征拍平到包含自定义项组件的children中
   * @param children 包含自定义项组的元数据信息(form/table的$children)
   * @param rootStore rootStore
   */
  flatUserDefines = (children: any[], rootStore: any) => {
    let userDefineIndex
    let userDefineNode
    // 设计器固化的字段, 不再重复渲染
    let excludeMap = new Map()

    const findNode = (node: any, path: any) => {
      if (node.controlType === USER_DEFINE_GROUP_KEY) {
        userDefineIndex = path
        userDefineNode = node
        if (node.existCharacterCodes?.length) {
          // 设计器固化的字段会记录到userdefinegroup节点上
          node.existCharacterCodes.forEach((existCharacterCode: string) => {
            excludeMap.set(`${existCharacterCode}_${node.entityUri}`, true)
          })
        }
      }

      if (node.children) {
        for (let [index, child] of node.children.entries()) {
          findNode(child, [...path, index]);
        }
      }
      return null;
    }
    findNode({ children }, [])

    // 不包含自定义项, 直接返回children
    if (!userDefineNode || !userDefineIndex) return children
    this.insertStructs2Children(rootStore, excludeMap, userDefineIndex, userDefineNode, children)
    return children
  }
  insertStructs2Children(rootStore: any, excludeMap: Map<string, boolean>, userDefineIndex: any, userDefineNode: any, children: any[]) {
    let structs: any[] = []
    const entityUri = userDefineNode.entityUri
    const userDefines = rootStore.pageStore.userDefines
    if (entityUri && userDefines) {
      Object.keys(userDefines).forEach(key => {
        const userDefine = userDefines[key]
        if (userDefine?.entityUri === entityUri) structs = userDefine.controls
      });
    }
    if (structs?.length) {
      // 有分配的自定义项
      const adaptedStructs = this.characterAdapter.adaptStructs({ structs, parentDesc: userDefineNode, terminalType: rootStore.pageStore.params.terminalType })
      // 设计器已经固化的字段过滤掉
      const filterStructs = adaptedStructs.filter((struct: any) => {
        return !excludeMap.get(`${struct.code}_${userDefineNode.entityUri}`)
      })

      if (userDefineIndex.length == 1) {
        children.splice(userDefineIndex, 1, ...filterStructs)
      } else {
        const end = userDefineIndex[userDefineIndex.length - 1]
        const path = userDefineIndex.map((i: number) => `[${i}]`).join('children').replace(/\[\d+\]$/, '')
        const newC = get(children, path)
        newC.splice(end, 1, ...filterStructs)
        set(children, path, newC)
      }
    }
  }
}
